import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { DangerousHtmlContent, Notification, variants } from '@eti/components';
import { useProperty, useSnowplowTracking, useTranslation } from '@eti/providers';
import { cx } from '@eti/styles';
import {
  BoundNew,
  TripCharacteristics as TripCharacteristicsType,
  TripScheduleChange,
} from '@eti/types';
import { ClimateCompensationNotification } from '@eti/products';
import { TripCharacteristics } from '@eti/schema-types';
import ImportantInformation from '../../../widgets/important-information/ImportantInformation';
import ScheduleNotification from '../../../widgets/trip-notification/components/ScheduleNotification';
import PriceDropProtectionNotification from '../../../common/components/PriceDropProtectionNotification';
import { getScheduleNotificationContent } from '../../../utils/notification';
import { getOperatingCarrierCodes } from '../utils/carriers';
import TrustInformationNotification from './notifications/TrustInformationNotification';
import AlwaysIncludedNotification from './notifications/AlwaysIncludedNotification';
import SelfTransferGuaranteeNotification from './notifications/SelfTransferGuaranteeNotification';
import PriceChangeNotification from './notifications/PriceChangeNotification';
import FreeCancellationDaysAheadNotification from './notifications/FreeCancellationDaysAheadNotification';
import SelfTransferDealsNotification from './notifications/SelfTransferDealsNotification';
import SelfTransferDealsDrawer from './SelfTransferDealsDrawer';

const TravelerDetailsNotifications = ({
  bounds,
  climateCompensationPrice,
  tripChange,
  tripCharacteristics = [],
}) => {
  const { p } = useProperty();
  const { t } = useTranslation();
  const { isSnowplowTrackingEnabled, sendTrackEvent } = useSnowplowTracking();
  const [isSelfTransferDealsDrawerOpen, setIsSelfTransferDealsDrawerOpen] = useState(false);

  const isTDSelfTransferInfoBannerEnabled = p('TravelerDetails.SelfTransfer.InfoBanner.Enabled');
  const isVISelfTansferDealEnabled = p('IbeClient.VI.SelfTransferDeal.Enabled');
  const selfTransferGuaranteeTestId = 'travelerDetailsPage-self-transfer-guarantee';

  useEffect(() => {
    if (isSnowplowTrackingEnabled) {
      const selfTransferAnchorElement = document.querySelector(
        `div[data-testid=${selfTransferGuaranteeTestId}] a`,
      );

      const onSelfTransferClick = () => {
        sendTrackEvent({
          category: 'Traveler Details',
          action: 'Click',
          label: 'Self-transfer more Info page link',
        });
      };

      selfTransferAnchorElement?.addEventListener('click', onSelfTransferClick, true);

      return () => {
        selfTransferAnchorElement?.removeEventListener('click', onSelfTransferClick);
      };
    }
    return null;
  }, [isSnowplowTrackingEnabled, sendTrackEvent]);

  return (
    <>
      <TrustInformationNotification bounds={bounds} />
      <ImportantInformation />
      {typeof climateCompensationPrice === 'number' && (
        <ClimateCompensationNotification priceRaw={climateCompensationPrice} />
      )}
      <AlwaysIncludedNotification operatingCarrierCodes={getOperatingCarrierCodes(bounds)} />
      {isVISelfTansferDealEnabled &&
      tripCharacteristics.includes(TripCharacteristics.VirtualInterlining) ? (
        <SelfTransferDealsNotification
          handleShowSelfTransferDealsDrawer={() => setIsSelfTransferDealsDrawerOpen(true)}
        />
      ) : (
        isTDSelfTransferInfoBannerEnabled && (
          <>
            <SelfTransferGuaranteeNotification
              dataTestId={selfTransferGuaranteeTestId}
              tripCharacteristics={tripCharacteristics}
            />
            {tripCharacteristics.includes(
              TripCharacteristics.VirtualInterliningWithShortConnectionTime,
            ) && (
              <Notification
                dataTestId="short-connection-time-notification"
                title={t('IbeClient.NoBagFare.Header')}
                variant={variants.WARNING}
              >
                <DangerousHtmlContent content={t('IbeClient.NoBagFare.Text')} />
              </Notification>
            )}
          </>
        )
      )}
      <PriceChangeNotification tripChange={tripChange} />
      {Boolean(tripChange?.schedule) && (
        <ScheduleNotification
          className={cx('scheduleNotification')}
          content={getScheduleNotificationContent(tripChange)}
          dataTestId="travelerDetails-scheduleChange-TripNotification"
        />
      )}
      {p('PriceDropProtection.DisplayBanners.Enabled') && (
        <PriceDropProtectionNotification
          linkText={t('BookingFlow.PriceDropProtection.LinkText')}
          text={t('BookingFlow.PriceDropProtection.Text')}
          title={t('BookingFlow.PriceDropProtection.Title')}
        />
      )}
      <FreeCancellationDaysAheadNotification bounds={bounds} />
      {isVISelfTansferDealEnabled && (
        <SelfTransferDealsDrawer
          isOpen={isSelfTransferDealsDrawerOpen}
          onClose={() => setIsSelfTransferDealsDrawerOpen(false)}
        />
      )}
    </>
  );
};

TravelerDetailsNotifications.propTypes = {
  bounds: PropTypes.arrayOf(BoundNew),
  climateCompensationPrice: PropTypes.number,
  tripChange: PropTypes.shape({
    price: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.shape({
        currency: PropTypes.string,
        priceNew: PropTypes.number,
        priceOld: PropTypes.number,
      }),
    ]),
    schedule: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.arrayOf(PropTypes.shape({ segments: PropTypes.arrayOf(TripScheduleChange) })),
    ]),
  }),
  tripCharacteristics: PropTypes.arrayOf(TripCharacteristicsType),
};

export default TravelerDetailsNotifications;
